import React, { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'sonner';
import { WAVE_LABELS, createNode, buildPayload } from '../../types/annotation';
import NodePalette from './NodePalette';
import AnnotationToolbar from './AnnotationToolbar';
import FractalBreadcrumb from './FractalBreadcrumb';

function clamp(v, min, max) {
  return Math.min(max, Math.max(min, v));
}

const STORAGE_KEY = 'wavekit.annotation';

function loadFromStorage(analysisId) {
  if (!analysisId) return null;
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY}.${analysisId}`);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function saveToStorage(analysisId, data) {
  if (!analysisId) return;
  try {
    localStorage.setItem(`${STORAGE_KEY}.${analysisId}`, JSON.stringify(data));
  } catch { /* ignore */ }
}

export default function AnnotationCanvas({
  chartImageUrl,
  analysisId,
  projectId,
  readOnly = false,
  onNodesChange,
}) {
  const [fractalStack, setFractalStack] = useState([
    { label: 'Root', nodes: [], parentSegment: null, canvasRegion: null },
  ]);
  const [activeLabel, setActiveLabel] = useState(null);
  const [dragState, setDragState] = useState(null);
  const [showLines, setShowLines] = useState(true);
  const [history, setHistory] = useState([]);
  const [future, setFuture] = useState([]);
  const [saving, setSaving] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [lineHover, setLineHover] = useState(null);
  const containerRef = useRef(null);
  const saveTimerRef = useRef(null);

  const currentLevel = fractalStack[fractalStack.length - 1];
  const nodes = currentLevel.nodes;
  const isFractalView = fractalStack.length > 1;

  useEffect(() => {
    if (!analysisId) return;
    const saved = loadFromStorage(analysisId);
    if (saved?.fractalStack) {
      setFractalStack(saved.fractalStack);
    } else if (saved?.nodes) {
      setFractalStack([{ label: 'Root', nodes: saved.nodes, parentSegment: null, canvasRegion: null }]);
    }
  }, [analysisId]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      setCanvasSize({ width: entry.contentRect.width, height: entry.contentRect.height });
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const persist = useCallback((updatedStack) => {
    if (!analysisId) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      setSaving(true);
      const payload = {
        fractalStack: updatedStack,
        canvas_width: canvasSize.width,
        canvas_height: canvasSize.height,
        updated_at: new Date().toISOString(),
      };
      // Draft dual-write: localStorage only (no fake network success).
      // Authoritative grade persist is POST /user-annotation via Annotate.jsx submit.
      saveToStorage(analysisId, payload);
      setSaving(false);
    }, 500);
  }, [analysisId, canvasSize]);

  const setNodesForCurrentLevel = useCallback((newNodes) => {
    const newStack = [...fractalStack];
    newStack[newStack.length - 1] = { ...currentLevel, nodes: newNodes };
    setFractalStack(newStack);
    const rootNodes = newStack[0].nodes;
    onNodesChange?.(rootNodes);
    persist(newStack);
  }, [fractalStack, currentLevel, onNodesChange, persist]);

  const pushNodes = useCallback((next) => {
    setHistory(h => [...h, nodes]);
    setFuture([]);
    setNodesForCurrentLevel(next);
  }, [nodes, setNodesForCurrentLevel]);

  const handleUndo = useCallback(() => {
    if (history.length === 0) return;
    const prev = history[history.length - 1];
    setFuture(f => [...f, nodes]);
    setHistory(h => h.slice(0, -1));
    setNodesForCurrentLevel(prev);
  }, [history, nodes, setNodesForCurrentLevel]);

  const handleRedo = useCallback(() => {
    if (future.length === 0) return;
    const next = future[future.length - 1];
    setHistory(h => [...h, nodes]);
    setFuture(f => f.slice(0, -1));
    setNodesForCurrentLevel(next);
  }, [future, nodes, setNodesForCurrentLevel]);

  const handleFractalNavigate = useCallback((targetIndex) => {
    if (targetIndex < 0 || targetIndex >= fractalStack.length) return;
    setHistory([]);
    setFuture([]);
    setFractalStack(fractalStack.slice(0, targetIndex + 1));
  }, [fractalStack]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.ctrlKey || e.metaKey) {
        if (e.key === 'z') { e.preventDefault(); handleUndo(); }
        if (e.key === 'y') { e.preventDefault(); handleRedo(); }
      }
      if (e.key === 'Escape') {
        if (isFractalView) {
          handleFractalNavigate(fractalStack.length - 2);
        } else {
          setActiveLabel(null);
        }
      }
      const num = parseInt(e.key, 10);
      if (num >= 1 && num <= 5 && !e.ctrlKey && !e.metaKey) {
        setActiveLabel(String(num));
      }
      const letterMap = { a: 'A', b: 'B', c: 'C' };
      if (letterMap[e.key.toLowerCase()] && !e.ctrlKey && !e.metaKey) {
        setActiveLabel(letterMap[e.key.toLowerCase()]);
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleUndo, handleRedo, isFractalView, fractalStack.length, handleFractalNavigate]);

  const handleCanvasClick = (e) => {
    if (readOnly || !activeLabel || dragState) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const node = createNode(activeLabel, x, y, canvasSize.width, canvasSize.height);
    pushNodes([...nodes, node]);
  };

  const handlePointerDown = (e, node) => {
    if (readOnly) return;
    e.preventDefault();
    e.stopPropagation();
    setDragState({ id: node.id, startX: e.clientX, startY: e.clientY, origX: node.x_px, origY: node.y_px });
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };

  const handlePointerMove = (e) => {
    if (!dragState) return;
    e.preventDefault();
    const dx = e.clientX - dragState.startX;
    const dy = e.clientY - dragState.startY;
    const nx = clamp(dragState.origX + dx, 0, canvasSize.width);
    const ny = clamp(dragState.origY + dy, 0, canvasSize.height);
    const newStack = [...fractalStack];
    newStack[newStack.length - 1] = {
      ...currentLevel,
      nodes: currentLevel.nodes.map(n => n.id === dragState.id ? { ...n, x_px: nx, y_px: ny } : n),
    };
    setFractalStack(newStack);
  };

  const handlePointerUp = (e) => {
    if (!dragState) return;
    e.preventDefault();
    const updated = nodes.map(n => {
      if (n.id !== dragState.id) return n;
      return {
        ...n,
        x_norm: canvasSize.width > 0 ? n.x_px / canvasSize.width : 0,
        y_norm: canvasSize.height > 0 ? n.y_px / canvasSize.height : 0,
      };
    });
    setHistory(h => [...h, nodes]);
    setFuture([]);
    setNodesForCurrentLevel(updated);
    setDragState(null);
  };

  const handleContextMenu = (e, nodeId) => {
    e.preventDefault();
    pushNodes(nodes.filter(n => n.id !== nodeId));
  };

  const handleClear = () => {
    if (nodes.length === 0) return;
    pushNodes([]);
  };

  const handleExport = () => {
    const payload = buildPayload(
      fractalStack[0].nodes,
      canvasSize.width,
      canvasSize.height,
      analysisId,
      projectId,
      fractalStack,
    );
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
    toast.success('Annotation payload copied to clipboard');
  };

  const handleLineClick = (startNode, endNode) => {
    if (readOnly) return;
    const segLabel = `Wave ${startNode.label}\u2192${endNode.label}`;
    const newLevel = {
      label: segLabel,
      nodes: [],
      parentSegment: { startNode: startNode.id, endNode: endNode.id },
      canvasRegion: {
        x: Math.min(startNode.x_px, endNode.x_px),
        y: Math.min(startNode.y_px, endNode.y_px),
        width: Math.abs(endNode.x_px - startNode.x_px),
        height: Math.abs(endNode.y_px - startNode.y_px),
      },
    };
    setHistory([]);
    setFuture([]);
    setFractalStack([...fractalStack, newLevel]);
    setActiveLabel(null);
    toast.success(`Zoomed into ${segLabel} \u2014 draw micro-waves here`);
  };

  const motiveNodes = nodes.filter(n => n.wave_type === 'motive').sort((a, b) => {
    const order = ['1', '2', '3', '4', '5'];
    return order.indexOf(a.label) - order.indexOf(b.label);
  });
  const correctiveNodes = nodes.filter(n => n.wave_type === 'corrective').sort((a, b) => {
    const order = ['A', 'B', 'C', 'X', 'Y', 'W'];
    return order.indexOf(a.label) - order.indexOf(b.label);
  });

  const renderClickableLines = (sortedNodes, color) => {
    if (!showLines || sortedNodes.length < 2) return null;
    const segments = [];
    for (let i = 0; i < sortedNodes.length - 1; i++) {
      const a = sortedNodes[i];
      const b = sortedNodes[i + 1];
      const key = `${a.id}-${b.id}`;
      const isHovered = lineHover === key;
      segments.push(
        <line
          key={key}
          x1={a.x_px} y1={a.y_px}
          x2={b.x_px} y2={b.y_px}
          stroke={isHovered ? '#ffffff' : color}
          strokeWidth={isHovered ? 3 : 1.5}
          strokeDasharray={isHovered ? 'none' : '6 4'}
          opacity={isHovered ? 0.9 : 0.5}
          style={{ cursor: readOnly ? 'default' : 'pointer', pointerEvents: 'stroke' }}
          onMouseEnter={() => !readOnly && setLineHover(key)}
          onMouseLeave={() => setLineHover(null)}
          onClick={(e) => {
            e.stopPropagation();
            if (!readOnly) handleLineClick(a, b);
          }}
        />
      );
    }
    return segments;
  };

  return (
    <div className="space-y-3">
      <FractalBreadcrumb stack={fractalStack} onNavigate={handleFractalNavigate} />

      {!readOnly && (
        <div className="glass-panel rounded p-3 space-y-3">
          <NodePalette activeLabel={activeLabel} onSelect={setActiveLabel} disabled={readOnly} />
          <AnnotationToolbar
            onUndo={handleUndo}
            onRedo={handleRedo}
            onClear={handleClear}
            onExport={handleExport}
            canUndo={history.length > 0}
            canRedo={future.length > 0}
            nodeCount={nodes.length}
            showLines={showLines}
            onToggleLines={() => setShowLines(!showLines)}
            saving={saving}
          />
          {isFractalView && (
            <div className="flex items-center gap-2 px-2 py-1.5 rounded bg-[#f5b400]/5 border border-[#f5b400]/15">
              <span className="text-[10px] text-[#f5b400] terminal-font font-bold">
                FRACTAL MODE: Drawing inside {currentLevel.label}
              </span>
              <button
                onClick={() => handleFractalNavigate(fractalStack.length - 2)}
                className="ml-auto text-[10px] text-[#8b93a7] hover:text-white terminal-font transition-colors"
              >
                Back to parent
              </button>
            </div>
          )}
        </div>
      )}

      <div
        ref={containerRef}
        className={`relative wk-chart-container select-none ${activeLabel ? 'cursor-crosshair' : ''}`}
        style={{ minHeight: 400 }}
        onClick={handleCanvasClick}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {chartImageUrl ? (
          <img
            src={chartImageUrl}
            alt="Chart for annotation"
            className="w-full h-auto block"
            draggable={false}
          />
        ) : (
          <div className="flex items-center justify-center h-[400px] text-[#8b93a7] font-mono text-sm">
            No chart loaded. Upload or select an analysis first.
          </div>
        )}

        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ overflow: 'visible' }}>
          <g style={{ pointerEvents: 'auto' }}>
            {renderClickableLines(motiveNodes, '#f5b400')}
            {renderClickableLines(correctiveNodes, '#00f5d4')}
          </g>
        </svg>

        {nodes.map((node) => {
          const isDragging = dragState?.id === node.id;
          return (
            <div
              key={node.id}
              className={`wk-annotation-node ${node.wave_type} ${isDragging ? 'dragging' : ''}`}
              style={{ left: node.x_px, top: node.y_px, zIndex: isDragging ? 50 : 10 }}
              onPointerDown={(e) => handlePointerDown(e, node)}
              onContextMenu={(e) => handleContextMenu(e, node.id)}
              title={`Wave ${node.label} (${node.wave_type}) - Right-click to remove, Click line to zoom fractal`}
            >
              {node.label}
            </div>
          );
        })}

        {isFractalView && (
          <div className="absolute top-2 left-2 px-2 py-1 rounded bg-[#f5b400]/10 border border-[#f5b400]/20">
            <span className="text-[9px] text-[#f5b400] terminal-font font-bold">
              FRACTAL: {currentLevel.label}
            </span>
          </div>
        )}
      </div>

      {!readOnly && !isFractalView && nodes.length >= 2 && (
        <div className="text-[10px] text-[#6b7280] terminal-font text-center">
          Click any line segment between two wave nodes to zoom in and draw micro-waves (fractals)
        </div>
      )}
    </div>
  );
}
